import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

interface Contact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  contact_type: string;
  source: string | null;
  services_received: string[];
  lifetime_value: number;
  cross_sell_status: Record<string, string | null>;
  do_not_contact: boolean;
  sms_opt_in: boolean;
  preferred_channel: string;
  tags: string[];
  last_contacted_at: string | null;
  last_job_closed_at: string | null;
  notes: string | null;
  created_at: string;
  leads: any[];
  jobs: any[];
  notes_list: any[];
}

const ALL_SERVICES = ['roofing', 'insurance', 'asphalt', 'concrete'];

const SERVICE_META: Record<string, { label: string; color: string; icon: string }> = {
  roofing:   { label: 'Roofing',   color: '#4A7EB5', icon: '🏠' },
  insurance: { label: 'Insurance', color: '#06B6D4', icon: '🛡️' },
  asphalt:   { label: 'Asphalt',   color: '#F59E0B', icon: '🛣️' },
  concrete:  { label: 'Concrete',  color: '#8B5CF6', icon: '🪨' },
};

const TYPE_META: Record<string, { label: string; color: string }> = {
  customer:   { label: 'Customer',   color: '#10B981' },
  prospect:   { label: 'Prospect',   color: '#4A7EB5' },
  cold_lead:  { label: 'Cold Lead',  color: '#6B7280' },
  applicant:  { label: 'Applicant',  color: '#8B5CF6' },
  referral:   { label: 'Referral',   color: '#F59E0B' },
};

const CROSS_SELL_STATUSES = [
  { value: '',               label: 'Not Contacted' },
  { value: 'pitched',        label: 'Pitched' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'converted',      label: 'Converted ✓' },
];

const LEAD_STATUS_META: Record<string, { label: string; color: string }> = {
  new:             { label: 'New',             color: '#4A7EB5' },
  contacted:       { label: 'Contacted',       color: '#F59E0B' },
  appointment_set: { label: 'Appt Set',        color: '#10B981' },
  lost:            { label: 'Lost',            color: '#EF4444' },
  converted:       { label: 'Converted',       color: '#10B981' },
  sold:            { label: 'Sold',            color: '#10B981' },
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const timeAgo = (iso: string | null) => {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#1A2B3C', borderRadius: 12, border: '1px solid #2D4259',
      padding: 20, marginBottom: 16,
    }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, color: '#8FA8C0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Contact>>({});
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);

  const fetchContact = async () => {
    try {
      const { data } = await api.get(`/contacts/${id}`);
      setContact({ ...data, notes_list: data.notes || [] });
      setNotes(data.notes || []);
      setEditForm({
        name: data.name,
        email: data.email,
        phone: data.phone,
        address: data.address,
        preferred_channel: data.preferred_channel,
        sms_opt_in: data.sms_opt_in,
      });
    } catch (err) {
      console.error('Failed to fetch contact:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContact(); }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/contacts/${id}`, editForm);
      setContact(c => c ? { ...c, ...data } : c);
      setEditing(false);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDNCToggle = async () => {
    if (!contact) return;
    const { data } = await api.patch(`/contacts/${id}`, { do_not_contact: !contact.do_not_contact });
    setContact(c => c ? { ...c, ...data } : c);
  };

  const handleCrossSell = async (service: string, status: string) => {
    try {
      const { data } = await api.patch(`/contacts/${id}/cross-sell`, { service, status: status || null });
      setContact(c => c ? { ...c, cross_sell_status: data.cross_sell_status } : c);
    } catch (err) {
      console.error('Cross-sell update failed:', err);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await api.post(`/contacts/${id}/notes`, { content: noteText });
      setNotes(n => [data, ...n]);
      setNoteText('');
    } catch (err) {
      console.error('Add note failed:', err);
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#8FA8C0' }}>
      Loading...
    </div>
  );

  if (!contact) return (
    <div style={{ padding: 40, color: '#EF4444' }}>Contact not found.</div>
  );

  const typeMeta = TYPE_META[contact.contact_type] || { label: contact.contact_type, color: '#6B7280' };
  const servicesToPitch = ALL_SERVICES.filter(s => !(contact.services_received || []).includes(s));

  return (
    <div style={{ padding: 24, color: '#f1f5f9', maxWidth: 1200, margin: '0 auto' }}>
      {/* Back */}
      <button
        onClick={() => navigate('/contacts')}
        style={{ background: 'none', border: 'none', color: '#8FA8C0', fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}
      >
        ← Contacts
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>{contact.name}</h1>
        <span style={{
          background: typeMeta.color + '22', color: typeMeta.color,
          borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700,
        }}>{typeMeta.label}</span>
        <button
          onClick={handleDNCToggle}
          style={{
            padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            background: contact.do_not_contact ? '#EF444422' : '#1A2B3C',
            color: contact.do_not_contact ? '#EF4444' : '#8FA8C0',
            border: contact.do_not_contact ? '1px solid #EF4444' : '1px solid #2D4259',
          } as any}
        >
          {contact.do_not_contact ? '🚫 Do Not Contact' : '✓ Contactable'}
        </button>
        <span style={{ color: '#4A6580', fontSize: 13, marginLeft: 'auto' }}>
          Added {formatDate(contact.created_at)}
        </span>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 16, alignItems: 'start' }}>

        {/* LEFT COLUMN */}
        <div>
          {/* Contact Info */}
          <Card title="Contact Info">
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { key: 'name', label: 'Name', type: 'text' },
                  { key: 'email', label: 'Email', type: 'email' },
                  { key: 'phone', label: 'Phone', type: 'tel' },
                  { key: 'address', label: 'Address', type: 'text' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 11, color: '#8FA8C0', display: 'block', marginBottom: 4 }}>{f.label}</label>
                    <input
                      type={f.type}
                      value={(editForm as any)[f.key] || ''}
                      onChange={e => setEditForm(ef => ({ ...ef, [f.key]: e.target.value }))}
                      style={{
                        width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 13,
                        border: '1px solid #2D4259', background: '#0F1923', color: '#f1f5f9', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ flex: 1, padding: '8px', borderRadius: 6, border: 'none', background: '#4A7EB5', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                  >{saving ? 'Saving...' : 'Save'}</button>
                  <button
                    onClick={() => setEditing(false)}
                    style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid #2D4259', background: 'transparent', color: '#8FA8C0', cursor: 'pointer', fontSize: 13 }}
                  >Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                {[
                  { label: 'Email',    value: contact.email },
                  { label: 'Phone',    value: contact.phone },
                  { label: 'Address',  value: contact.address },
                  { label: 'Source',   value: contact.source },
                  { label: 'Channel',  value: contact.preferred_channel },
                  { label: 'SMS Opt-In', value: contact.sms_opt_in ? 'Yes' : 'No' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 12, color: '#8FA8C0', width: 90, flexShrink: 0 }}>{row.label}</span>
                    <span style={{ fontSize: 13, color: row.value ? '#f1f5f9' : '#4A6580' }}>{row.value || '—'}</span>
                  </div>
                ))}
                <button
                  onClick={() => setEditing(true)}
                  style={{ marginTop: 8, padding: '6px 14px', borderRadius: 6, border: '1px solid #2D4259', background: 'transparent', color: '#8FA8C0', cursor: 'pointer', fontSize: 12 }}
                >✏️ Edit</button>
              </div>
            )}
          </Card>

          {/* Jobs */}
          <Card title={`Jobs (${(contact.jobs || []).length})`}>
            {(contact.jobs || []).length === 0 ? (
              <p style={{ color: '#4A6580', fontSize: 13, margin: 0 }}>No jobs yet.</p>
            ) : (
              (contact.jobs || []).map((job: any) => {
                const sm = SERVICE_META[job.job_type];
                const val = job.approved_amount || job.total_revenue || job.total_price;
                return (
                  <div
                    key={job.id}
                    onClick={() => navigate(`/pipeline/${job.id}`)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 0', borderBottom: '1px solid #1e3448', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {sm && (
                        <span style={{
                          background: sm.color + '22', color: sm.color,
                          borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                        }}>{sm.icon} {sm.label}</span>
                      )}
                      <span style={{ fontSize: 13, color: '#8FA8C0' }}>{job.pipeline_stage_name}</span>
                      {job.job_number && <span style={{ fontSize: 11, color: '#4A6580' }}>#{job.job_number}</span>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {val && <div style={{ fontSize: 13, fontWeight: 600, color: '#10B981' }}>${Number(val).toLocaleString()}</div>}
                      <div style={{ fontSize: 11, color: '#4A6580' }}>{formatDate(job.created_at)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </Card>

          {/* Leads */}
          <Card title={`Leads (${(contact.leads || []).length})`}>
            {(contact.leads || []).length === 0 ? (
              <p style={{ color: '#4A6580', fontSize: 13, margin: 0 }}>No leads.</p>
            ) : (
              (contact.leads || []).map((lead: any) => {
                const sm = SERVICE_META[lead.job_type];
                const statusMeta = LEAD_STATUS_META[lead.lead_status || lead.status] || { label: lead.lead_status || lead.status || 'Unknown', color: '#6B7280' };
                return (
                  <div key={lead.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 0', borderBottom: '1px solid #1e3448',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {sm && (
                        <span style={{ background: sm.color + '22', color: sm.color, borderRadius: 4, padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>
                          {sm.icon} {sm.label}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: '#8FA8C0', textTransform: 'capitalize' }}>
                        {lead.lead_source || 'manual'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ background: statusMeta.color + '22', color: statusMeta.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                        {statusMeta.label}
                      </span>
                      <span style={{ fontSize: 11, color: '#4A6580' }}>{formatDate(lead.created_at)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </Card>

          {/* Notes */}
          <Card title="Notes">
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note..."
                rows={2}
                style={{
                  flex: 1, padding: '8px 10px', borderRadius: 6, fontSize: 13, resize: 'vertical',
                  border: '1px solid #2D4259', background: '#0F1923', color: '#f1f5f9',
                }}
              />
              <button
                onClick={handleAddNote}
                disabled={addingNote || !noteText.trim()}
                style={{
                  padding: '8px 14px', borderRadius: 6, border: 'none',
                  background: '#4A7EB5', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13,
                  alignSelf: 'flex-end',
                }}
              >{addingNote ? '...' : 'Add'}</button>
            </div>
            {notes.length === 0 ? (
              <p style={{ color: '#4A6580', fontSize: 13, margin: 0 }}>No notes yet.</p>
            ) : (
              notes.map((note: any) => (
                <div key={note.id} style={{ padding: '10px 0', borderBottom: '1px solid #1e3448' }}>
                  <div style={{ fontSize: 13, color: '#f1f5f9', marginBottom: 4 }}>{note.content}</div>
                  <div style={{ fontSize: 11, color: '#4A6580' }}>
                    {note.users?.name || 'Unknown'} · {formatDate(note.created_at)}
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div>
          {/* Lifetime Value */}
          <div style={{
            background: '#1A2B3C', borderRadius: 12, border: '1px solid #2D4259',
            padding: 20, marginBottom: 16, textAlign: 'center',
          }}>
            <div style={{ fontSize: 11, color: '#8FA8C0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Lifetime Value</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: contact.lifetime_value > 0 ? '#10B981' : '#4A6580' }}>
              {contact.lifetime_value > 0 ? `$${Number(contact.lifetime_value).toLocaleString()}` : '$0'}
            </div>
            <div style={{ fontSize: 12, color: '#4A6580', marginTop: 4 }}>
              {(contact.jobs || []).length} job{(contact.jobs || []).length !== 1 ? 's' : ''}
              {contact.last_job_closed_at && ` · Last closed ${timeAgo(contact.last_job_closed_at)}`}
            </div>
          </div>

          {/* Cross-Sell */}
          <Card title="Cross-Sell Opportunities">
            {/* Services received */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#8FA8C0', marginBottom: 8 }}>SERVICES RECEIVED</div>
              {(contact.services_received || []).length === 0 ? (
                <span style={{ color: '#4A6580', fontSize: 13 }}>None yet</span>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(contact.services_received || []).map(s => {
                    const sm = SERVICE_META[s];
                    if (!sm) return null;
                    return (
                      <span key={s} style={{
                        background: sm.color + '22', color: sm.color,
                        borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700,
                      }}>{sm.icon} {sm.label}</span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Services to pitch */}
            <div>
              <div style={{ fontSize: 11, color: '#8FA8C0', marginBottom: 8 }}>PITCH NEXT</div>
              {servicesToPitch.length === 0 ? (
                <p style={{ color: '#10B981', fontSize: 13, margin: 0 }}>All services received! 🎉</p>
              ) : (
                servicesToPitch.map(service => {
                  const sm = SERVICE_META[service];
                  const currentStatus = contact.cross_sell_status?.[service] || '';
                  return (
                    <div key={service} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 0', borderBottom: '1px solid #1e3448',
                    }}>
                      <span style={{
                        background: sm.color + '22', color: sm.color,
                        borderRadius: 4, padding: '3px 8px', fontSize: 12, fontWeight: 700,
                      }}>{sm.icon} {sm.label}</span>
                      <select
                        value={currentStatus}
                        onChange={e => handleCrossSell(service, e.target.value)}
                        style={{
                          padding: '4px 8px', borderRadius: 6, fontSize: 12,
                          border: '1px solid #2D4259', background: '#0F1923', color: '#f1f5f9',
                        }}
                      >
                        {CROSS_SELL_STATUSES.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          {/* Tags */}
          {(contact.tags || []).length > 0 && (
            <Card title="Tags">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {contact.tags.map(tag => (
                  <span key={tag} style={{
                    background: '#2D4259', color: '#8FA8C0',
                    borderRadius: 4, padding: '3px 8px', fontSize: 11,
                  }}>{tag}</span>
                ))}
              </div>
            </Card>
          )}

          {/* Quick stats */}
          <Card title="Activity">
            {[
              { label: 'Added',          value: formatDate(contact.created_at) },
              { label: 'Last Contacted', value: timeAgo(contact.last_contacted_at) },
              { label: 'Last Job',       value: timeAgo(contact.last_job_closed_at) },
              { label: 'Source',         value: contact.source || '—' },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#8FA8C0' }}>{row.label}</span>
                <span style={{ fontSize: 12, color: '#f1f5f9', textTransform: 'capitalize' }}>{row.value}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
